/**
 * Public-map road contribution wizard — see ROAD_LANE_DIRECTION_POLICY §2.14
 */

import { MOTOR_ROAD_CATEGORIES } from './roadSideFacilities';
import {
  dividedNeedsDivideTrace,
  stripNeedsMapTrace,
  type StripCaptureMode,
} from './roadStripSurvey';

export type RoadMarkingTarget = 'divide' | 'strip_a' | 'strip_b' | 'single_strip';

export type RoadGeometrySegmentStatus = 'required' | 'derived' | 'complete' | 'invalid';

export function usesMotorRoadSurveyFlow(
  featureTypeId: string | undefined,
  category: string | null | undefined,
): boolean {
  if (featureTypeId !== 'road' || !category) return false;
  return MOTOR_ROAD_CATEGORIES.has(category);
}

/** Capture plan only records strip capture modes; widths are validated in Details. */
export function validateRoadCapturePlan(_input: {
  carriagewayCount: number;
  singleStripCaptureMode: StripCaptureMode;
  stripACaptureMode: StripCaptureMode;
  stripBCaptureMode: StripCaptureMode;
}): string | null {
  return null;
}

export function buildRoadMarkingQueue(input: {
  carriagewayCount: number | null;
  singleStripCaptureMode: StripCaptureMode;
  stripACaptureMode: StripCaptureMode;
  stripBCaptureMode: StripCaptureMode;
  hasDivideLine: boolean;
  hasStripReferenceA: boolean;
  hasStripReferenceB: boolean;
  hasSingleStripReference: boolean;
}): RoadMarkingTarget[] {
  const { carriagewayCount } = input;
  if (carriagewayCount == null) return [];

  if (carriagewayCount === 1) {
    if (stripNeedsMapTrace(input.singleStripCaptureMode) && !input.hasSingleStripReference) {
      return ['single_strip'];
    }
    return [];
  }

  const queue: RoadMarkingTarget[] = [];
  const needsDivide = dividedNeedsDivideTrace(input.stripACaptureMode, input.stripBCaptureMode);
  if (needsDivide && !input.hasDivideLine) {
    queue.push('divide');
  }
  if (stripNeedsMapTrace(input.stripACaptureMode) && !input.hasStripReferenceA) {
    queue.push('strip_a');
  }
  if (stripNeedsMapTrace(input.stripBCaptureMode) && !input.hasStripReferenceB) {
    queue.push('strip_b');
  }
  return queue;
}

export function nextRoadMarkingTarget(input: Parameters<typeof buildRoadMarkingQueue>[0]): RoadMarkingTarget | null {
  const q = buildRoadMarkingQueue(input);
  return q.length > 0 ? q[0] : null;
}

export function roadLayoutLabel(carriagewayCount: number | null): string {
  if (carriagewayCount === 1) return 'Single carriageway';
  if (carriagewayCount != null && carriagewayCount >= 2) return 'Divided carriageway';
  return '';
}

export function geometryStatusLabel(status: RoadGeometrySegmentStatus): string {
  switch (status) {
    case 'complete':
      return 'Complete';
    case 'derived':
      return 'Derived';
    case 'required':
      return 'Required';
    case 'invalid':
      return 'Needs update';
    default:
      return status;
  }
}

export function computeRoadGeometryStatusRows(input: {
  carriagewayCount: number | null;
  singleStripCaptureMode: StripCaptureMode;
  stripACaptureMode: StripCaptureMode;
  stripBCaptureMode: StripCaptureMode;
  dividedDivideReady: boolean;
  dividedCarriagewayAReady: boolean;
  dividedCarriagewayBReady: boolean;
  singleStripTraceReady: boolean;
}): { label: string; status: RoadGeometrySegmentStatus }[] {
  if (input.carriagewayCount == null) return [];

  if (input.carriagewayCount === 1) {
    if (input.singleStripCaptureMode === 'derive_from_divide') {
      return [{ label: 'Carriageway', status: 'invalid' }];
    }
    return [
      {
        label: 'Carriageway',
        status: input.singleStripTraceReady ? 'complete' : 'required',
      },
    ];
  }

  const rows: { label: string; status: RoadGeometrySegmentStatus }[] = [];
  const needsDivide = dividedNeedsDivideTrace(input.stripACaptureMode, input.stripBCaptureMode);
  if (needsDivide) {
    rows.push({
      label: 'Divide',
      status: input.dividedDivideReady ? 'complete' : 'required',
    });
  } else if (input.dividedDivideReady) {
    rows.push({ label: 'Divide', status: 'complete' });
  }

  const bothDeriveFromDivide =
    input.stripACaptureMode === 'derive_from_divide'
    && input.stripBCaptureMode === 'derive_from_divide';

  if (bothDeriveFromDivide) {
    if (input.dividedDivideReady) {
      rows.push({
        label: 'Carriageways A & B',
        status: 'derived',
      });
    }
    return rows;
  }

  const statusA = ((): RoadGeometrySegmentStatus => {
    if (input.stripACaptureMode === 'derive_from_divide') {
      return input.dividedDivideReady ? 'derived' : 'required';
    }
    return input.dividedCarriagewayAReady ? 'complete' : 'required';
  })();
  const statusB = ((): RoadGeometrySegmentStatus => {
    if (input.stripBCaptureMode === 'derive_from_divide') {
      return input.dividedDivideReady ? 'derived' : 'required';
    }
    return input.dividedCarriagewayBReady ? 'complete' : 'required';
  })();

  rows.push({ label: 'Carriageway A', status: statusA });
  rows.push({ label: 'Carriageway B', status: statusB });
  return rows;
}
